import { useState } from 'react';
import type { ReactNode } from 'react';
import { HomeIcon, IdCardIcon, KeyRound, LogOut, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { VIEWS } from '@/config/views';
import {
  isNavPathActive,
  MenuIcon,
  menuLeaves,
  pruneMenu,
  useMenu,
  type MenuNode,
} from '@/features/navigation';
import { cn } from '@/shared/lib/cn';

import { MoreMenu } from './MoreMenu';

/**
 * Material and the HIG both cap a bottom bar at five destinations. Up to five
 * fit as-is; past that, four tabs and «Más» take the five slots — so a worker
 * with four entries never gets a More menu holding a single item.
 */
const MAX_TABS = 5;

interface Tab {
  key: string;
  path: string;
  label: string;
  icon: ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Tab link (icon + label, active state)                               */
/* ------------------------------------------------------------------ */

function TabLink({ tab, allPaths }: { tab: Tab; allPaths: readonly string[] }) {
  const { pathname } = useLocation();
  const active = isNavPathActive(pathname, tab.path, allPaths);
  return (
    <Link
      to={tab.path}
      aria-current={active ? 'page' : undefined}
      className={cn(
        '[&>svg]:size-6 flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors',
        active ? 'text-brand-900 font-medium' : 'text-ink-muted active:text-ink',
      )}
    >
      {tab.icon}
      <span className="max-w-18 truncate">{tab.label}</span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Overflow tree inside the More menu                                 */
/* ------------------------------------------------------------------ */

function OverflowNodes({
  nodes,
  allPaths,
  onNavigate,
}: {
  nodes: readonly MenuNode[];
  allPaths: readonly string[];
  onNavigate: () => void;
}) {
  const { pathname } = useLocation();
  return nodes.map((node) => {
    if (node.path === null) {
      // Groups become section headers rather than accordions: inside a bottom
      // sheet one more tap to expand is the cost the drawer pattern avoids.
      return (
        <div key={node.code} className="mt-1">
          <p className="text-ink-muted px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide">
            {node.label}
          </p>
          <OverflowNodes nodes={node.children} allPaths={allPaths} onNavigate={onNavigate} />
        </div>
      );
    }
    const active = isNavPathActive(pathname, node.path, allPaths);
    return (
      <Link
        key={node.code}
        to={node.path}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          '[&>svg]:size-6 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
          active ? 'bg-brand-50 text-brand-900 font-medium' : 'text-ink hover:bg-surface-sunken',
        )}
      >
        <MenuIcon name={node.icon} />
        {node.label}
      </Link>
    );
  });
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function MobileTabBar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const nodes = useMenu().data ?? [];

  // Same source as the sidebar: the server picks the entries, priority picks
  // which leaves earn a tab, and the rest keep their groups in the More menu.
  // The sort is stable, so equal priorities keep the menu's own order.
  const leafTabs: Tab[] = menuLeaves(nodes)
    .map((leaf, index) => ({ leaf, index }))
    .sort((a, b) => (b.leaf.mobile_priority ?? 0) - (a.leaf.mobile_priority ?? 0))
    .map(({ leaf }) => ({
      key: leaf.code,
      path: leaf.path,
      label: leaf.label,
      icon: <MenuIcon name={leaf.icon} />,
    }));
  const tabs: Tab[] = [
    { key: 'home', path: VIEWS.home.path, label: t('nav.home'), icon: <HomeIcon /> },
    ...leafTabs,
  ];
  const allPaths = tabs.map((tab) => tab.path);

  const primary = tabs.length <= MAX_TABS ? tabs : tabs.slice(0, MAX_TABS - 1);
  const primaryKeys = new Set(primary.map((tab) => tab.key));
  const overflow = pruneMenu(nodes, (leaf) => !primaryKeys.has(leaf.code));

  const closeMore = () => setMoreOpen(false);

  return (
    <>
      <MoreMenu open={moreOpen} onClose={closeMore}>
        <div className="border-border my-1 border-t" />

        {/* Navigation overflow, grouped as in the sidebar */}
        <OverflowNodes nodes={overflow} allPaths={allPaths} onNavigate={closeMore} />

        <div className="border-border my-1 border-t" />

        {/* Account actions */}
        {user && (
          <>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void navigate('/mi-ficha');
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <IdCardIcon />
              {t('auth:user.myAccount')}
            </button>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void navigate('/cambiar-contrasena');
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <KeyRound />
              {t('auth:user.changePassword')}
            </button>
            <button
              type="button"
              onClick={() => {
                closeMore();
                void logout();
              }}
              className="text-ink hover:bg-surface-sunken flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors"
            >
              <LogOut />
              {t('auth:logout.action')}
            </button>
          </>
        )}
      </MoreMenu>

      {/* Visible: primary tabs + More button */}
      <nav className="bg-surface border-border fixed bottom-0 inset-x-0 z-50 flex items-stretch border-t md:hidden">
        {primary.map((tab) => (
          <TabLink key={tab.key} tab={tab} allPaths={allPaths} />
        ))}
        {overflow.length > 0 && (
          <button
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] transition-colors',
              moreOpen ? 'text-brand-900 font-medium' : 'text-ink-muted active:text-ink',
            )}
          >
            <MoreHorizontal className="size-5" aria-hidden />
            <span>{t('nav.more')}</span>
          </button>
        )}
      </nav>
    </>
  );
}
